import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  useCourtTypes,
  useCreateQuestionnaire,
  useQuestionnairesList,
} from '../hooks/useQuestionnaires';
import { BACKEND_FEATURES } from '../../../platform/backendFeatures';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (slug: string) => void;
}

// slugify drops anything that's not a-z/0-9 and collapses spaces to dashes.
// Cheap client-side normalizer for the auto-fill; the API still owns the
// canonical (tenant_id, slug) uniqueness check.
const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const NewQuestionnaireDialog = ({ open, onClose, onCreated }: Props) => {
  const courtTypes = useCourtTypes();
  // Copy-source picker draws from active questionnaires in the current
  // tenant. Disabled ones are hidden — you can re-enable them through the
  // top picker if you really need to copy from one.
  const sources = useQuestionnairesList(false);
  const create = useCreateQuestionnaire();

  const [title, setTitle] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [term, setTerm] = useState('');
  const [intro, setIntro] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [courtTypeId, setCourtTypeId] = useState('');
  const [sourceSlug, setSourceSlug] = useState('');

  const effectiveSlug = slug || slugify(`${jurisdiction}-${term}`);

  const reset = () => {
    setTitle('');
    setJurisdiction('');
    setTerm('');
    setIntro('');
    setSlug('');
    setSlugTouched(false);
    setCourtTypeId('');
    setSourceSlug('');
  };

  const submit = async () => {
    if (!title || !jurisdiction || !term || !effectiveSlug) return;
    try {
      const newSlug = await create.mutateAsync({
        slug: effectiveSlug,
        jurisdiction,
        term,
        title,
        intro,
        courtTypeId: courtTypeId || undefined,
        sourceSlug: sourceSlug || undefined,
      });
      onCreated(newSlug);
      reset();
    } catch {
      /* error surfaces via create.error below */
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New Questionnaire</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            // Dictionary.tblQuestionnaireCustom.Name is varchar(50) - this maps directly to it.
            slotProps={{ htmlInput: { maxLength: 50 } }}
            helperText={`${title.length}/50`}
          />
          <TextField
            label="Jurisdiction"
            value={jurisdiction}
            onChange={(e) => setJurisdiction(e.target.value)}
            required
            fullWidth
            helperText="e.g. Bexar County Civil"
          />
          <TextField
            label="Term"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            required
            fullWidth
            helperText="e.g. Spring 2026"
          />
          <TextField
            label="Slug"
            value={slugTouched ? slug : effectiveSlug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugTouched(true);
            }}
            required
            fullWidth
            helperText={
              BACKEND_FEATURES.clientChosenSlugOnCreate
                ? 'URL-safe id. Auto-generated from jurisdiction + term, override if needed. Must be unique.'
                : "Auto-generated from jurisdiction + term. The API assigns its own id on create and doesn't preserve this value yet — you'll be taken to whatever id it returns."
            }
          />
          <TextField
            label="Intro (optional)"
            value={intro}
            onChange={(e) => setIntro(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          <TextField
            select
            label="Copy from existing (optional)"
            value={sourceSlug}
            onChange={(e) => setSourceSlug(e.target.value)}
            fullWidth
            helperText="Start with another questionnaire's sections. Question IDs are regenerated so the copy is independent."
          >
            <MenuItem value="">
              <em>Start blank</em>
            </MenuItem>
            {sources.data?.map((q) => (
              <MenuItem key={q.slug} value={q.slug}>
                {q.title}{' '}
                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                  ({q.jurisdiction} · {q.term})
                </Typography>
              </MenuItem>
            ))}
          </TextField>
          {BACKEND_FEATURES.courtTypeAssignment && (
            <TextField
              select
              label="Court Type (optional)"
              value={courtTypeId}
              onChange={(e) => setCourtTypeId(e.target.value)}
              fullWidth
              helperText="Linking lets the juror app route summons to this questionnaire."
            >
              <MenuItem value="">
                <em>Not linked yet</em>
              </MenuItem>
              {courtTypes.data?.map((ct) => (
                <MenuItem key={ct.id} value={ct.id}>
                  {ct.label} ({ct.code})
                </MenuItem>
              ))}
            </TextField>
          )}
          {create.isError && (
            <Alert severity="error" role="alert">
              {(create.error as Error)?.message ?? 'Create failed'}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { reset(); onClose(); }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={
            !title ||
            !jurisdiction ||
            !term ||
            !effectiveSlug ||
            create.isPending
          }
        >
          {create.isPending ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
